import { FunctionComponent } from 'react'
import { useTranslation } from 'react-i18next'
import styles from './taskNoteFilter.module.scss'
import { useAuth } from '@/modules/context/AuthContext.tsx'
import Checkbox from '@/components/_custom/Checkbox/Checkbox.tsx'
import { TaskNoteStatus, TaskNoteType } from '@/types/TaskNote.ts'

interface TaskNoteFilterProps {
    typeFilters: string[]
    setTypeFilters: (filters: string[]) => void
    statusFilters: string[]
    setStatusFilters: (filters: string[]) => void
    countsByType: Record<string, number>
    countsByStatus: Record<string, number>
}

const TaskNoteFilter: FunctionComponent<TaskNoteFilterProps> = ({
    typeFilters,
    setTypeFilters,
    statusFilters,
    setStatusFilters,
    countsByType,
    countsByStatus
}: TaskNoteFilterProps) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    function handleChangeType(type: TaskNoteType, checked: boolean): void {
        if (!checked) {
            const newFilters: string[] = typeFilters.filter((filter: string): boolean => filter !== type)
            setTypeFilters([])
            setTypeFilters(newFilters)
        } else {
            setTypeFilters([...typeFilters, type])
        }
    }

    function handleChangeStatus(status: string, checked: boolean): void {
        if (!checked) {
            const newFilters: string[] = statusFilters.filter((filter: string): boolean => filter !== status)
            setStatusFilters([])
            setStatusFilters(newFilters)
        } else {
            setStatusFilters([...statusFilters, status])
        }
    }

    return (
        <div className={`${styles.Container} ${user && user?.daycares.length > 1 && styles.AddTopMargin}`}>
            <p className={styles.FilterTitle}>{t('type')}</p>
            <div className={styles.CheckboxContainer}>
                <Checkbox
                    text={`${t('task')} ${countsByType['task'] ? `(${countsByType['task']})` : ''}`}
                    checked={typeFilters.includes(TaskNoteType.task)}
                    onChange={(checked: boolean): void => handleChangeType(TaskNoteType.task, checked)}
                />
                {typeFilters.includes(TaskNoteType.task) && (
                    <div className={styles.SubCheckbox}>
                        <Checkbox
                            text={`${t('todo')} ${countsByStatus['todo'] ? `(${countsByStatus['todo']})` : ''}`}
                            checked={statusFilters.includes(TaskNoteStatus.todo)}
                            onChange={(checked: boolean): void => handleChangeStatus(TaskNoteStatus.todo, checked)}
                            size={'small'}
                            className={styles.SmallCheckBox}
                        />
                        <Checkbox
                            text={`${t('inProgress')} ${countsByStatus['inProgress'] ? `(${countsByStatus['inProgress']})` : ''}`}
                            checked={statusFilters.includes(TaskNoteStatus.inProgress)}
                            onChange={(checked: boolean): void =>
                                handleChangeStatus(TaskNoteStatus.inProgress, checked)
                            }
                            size={'small'}
                            className={styles.SmallCheckBox}
                        />
                        <Checkbox
                            text={`${t('done')} ${countsByStatus['done'] ? `(${countsByStatus['done']})` : ''}`}
                            checked={statusFilters.includes(TaskNoteStatus.done)}
                            onChange={(checked: boolean): void => handleChangeStatus(TaskNoteStatus.done, checked)}
                            size={'small'}
                            className={styles.SmallCheckBox}
                        />
                    </div>
                )}
                <Checkbox
                    text={`${t('note')} ${countsByType['note'] ? `(${countsByType['note']})` : ''}`}
                    checked={typeFilters.includes(TaskNoteType.note)}
                    onChange={(checked: boolean): void => handleChangeType(TaskNoteType.note, checked)}
                />
                <Checkbox
                    text={`${t('call')} ${countsByType['call'] ? `(${countsByType['call']})` : ''}`}
                    checked={typeFilters.includes(TaskNoteType.call)}
                    onChange={(checked: boolean): void => handleChangeType(TaskNoteType.call, checked)}
                />
                {typeFilters.includes(TaskNoteType.call) && (
                    <div className={styles.SubCheckbox}>
                        <Checkbox
                            text={`${t('connected')} ${countsByStatus['connected'] ? `(${countsByStatus['connected']})` : ''}`}
                            checked={statusFilters.includes(TaskNoteStatus.connected)}
                            onChange={(checked: boolean): void => handleChangeStatus(TaskNoteStatus.connected, checked)}
                            size={'small'}
                            className={styles.SmallCheckBox}
                        />
                        <Checkbox
                            text={`${t('noAnswer')} ${countsByStatus['noAnswer'] ? `(${countsByStatus['noAnswer']})` : ''}`}
                            checked={statusFilters.includes(TaskNoteStatus.noAnswer)}
                            onChange={(checked: boolean): void => handleChangeStatus(TaskNoteStatus.noAnswer, checked)}
                            size={'small'}
                            className={styles.SmallCheckBox}
                        />
                        <Checkbox
                            text={`${t('leftMessage')} ${countsByStatus['leftMessage'] ? `(${countsByStatus['leftMessage']})` : ''}`}
                            checked={statusFilters.includes(TaskNoteStatus.leftMessage)}
                            onChange={(checked: boolean): void =>
                                handleChangeStatus(TaskNoteStatus.leftMessage, checked)
                            }
                            size={'small'}
                            className={styles.SmallCheckBox}
                        />
                    </div>
                )}
                <Checkbox
                    text={`${t('appointmentInPerson')} ${countsByType['appointmentInPerson'] ? `(${countsByType['appointmentInPerson']})` : ''}`}
                    checked={typeFilters.includes(TaskNoteType.appointmentInPerson)}
                    onChange={(checked: boolean): void => handleChangeType(TaskNoteType.appointmentInPerson, checked)}
                />
                {typeFilters.includes(TaskNoteType.appointmentInPerson) && (
                    <div className={styles.SubCheckbox}>
                        <Checkbox
                            text={`${t('schedule')} ${countsByStatus['schedule'] ? `(${countsByStatus['schedule']})` : ''}`}
                            checked={statusFilters.includes(TaskNoteStatus.schedule)}
                            onChange={(checked: boolean): void => handleChangeStatus(TaskNoteStatus.schedule, checked)}
                            size={'small'}
                            className={styles.SmallCheckBox}
                        />
                        <Checkbox
                            text={`${t('done')} ${countsByStatus['done' + ' ' + TaskNoteType.appointmentInPerson] ? `(${countsByStatus['done' + ' ' + TaskNoteType.appointmentInPerson]})` : ''}`}
                            checked={statusFilters.includes(
                                TaskNoteStatus.done + ' ' + TaskNoteType.appointmentInPerson
                            )}
                            onChange={(checked: boolean): void =>
                                handleChangeStatus(
                                    TaskNoteStatus.done + ' ' + TaskNoteType.appointmentInPerson,
                                    checked
                                )
                            }
                            size={'small'}
                            className={styles.SmallCheckBox}
                        />
                        <Checkbox
                            text={`${t('missed')} ${countsByStatus['missed'] ? `(${countsByStatus['missed']})` : ''}`}
                            checked={statusFilters.includes(TaskNoteStatus.missed)}
                            onChange={(checked: boolean): void => handleChangeStatus(TaskNoteStatus.missed, checked)}
                            size={'small'}
                            className={styles.SmallCheckBox}
                        />
                        <Checkbox
                            text={`${t('cancelled')} ${countsByStatus['cancelled'] ? `(${countsByStatus['cancelled']})` : ''}`}
                            checked={statusFilters.includes(TaskNoteStatus.cancelled)}
                            onChange={(checked: boolean): void => handleChangeStatus(TaskNoteStatus.cancelled, checked)}
                            size={'small'}
                            className={styles.SmallCheckBox}
                        />
                    </div>
                )}
                <Checkbox
                    text={`${t('appointmentVideoconference')} ${countsByType['appointmentVideoconference'] ? `(${countsByType['appointmentVideoconference']})` : ''}`}
                    checked={typeFilters.includes(TaskNoteType.appointmentVideoconference)}
                    onChange={(checked: boolean): void =>
                        handleChangeType(TaskNoteType.appointmentVideoconference, checked)
                    }
                />
                {typeFilters.includes(TaskNoteType.appointmentVideoconference) && (
                    <div className={styles.SubCheckbox}>
                        <Checkbox
                            text={`${t('schedule')} ${countsByStatus['schedule'] ? `(${countsByStatus['schedule']})` : ''}`}
                            checked={statusFilters.includes(TaskNoteStatus.schedule)}
                            onChange={(checked: boolean): void => handleChangeStatus(TaskNoteStatus.schedule, checked)}
                            size={'small'}
                            className={styles.SmallCheckBox}
                        />
                        <Checkbox
                            text={`${t('done')} ${countsByStatus['done' + ' ' + TaskNoteType.appointmentVideoconference] ? `(${countsByStatus['done' + ' ' + TaskNoteType.appointmentVideoconference]})` : ''}`}
                            checked={statusFilters.includes(
                                TaskNoteStatus.done + ' ' + TaskNoteType.appointmentVideoconference
                            )}
                            onChange={(checked: boolean): void =>
                                handleChangeStatus(
                                    TaskNoteStatus.done + ' ' + TaskNoteType.appointmentVideoconference,
                                    checked
                                )
                            }
                            size={'small'}
                            className={styles.SmallCheckBox}
                        />
                        <Checkbox
                            text={`${t('missed')} ${countsByStatus['missed'] ? `(${countsByStatus['missed']})` : ''}`}
                            checked={statusFilters.includes(TaskNoteStatus.missed)}
                            onChange={(checked: boolean): void => handleChangeStatus(TaskNoteStatus.missed, checked)}
                            size={'small'}
                            className={styles.SmallCheckBox}
                        />
                        <Checkbox
                            text={`${t('cancelled')} ${countsByStatus['cancelled'] ? `(${countsByStatus['cancelled']})` : ''}`}
                            checked={statusFilters.includes(TaskNoteStatus.cancelled)}
                            onChange={(checked: boolean): void => handleChangeStatus(TaskNoteStatus.cancelled, checked)}
                            size={'small'}
                            className={styles.SmallCheckBox}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}

export default TaskNoteFilter
